import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, Stack } from '@mui/material';
import HeadTitle from '../../components/HeadTitle';
import Loader from '../../components/common/Loader/Loader';
import { getRequest } from '../../utils/axiosUtils';
import styles from './styles';
import {
  RAW, TIERS, RATIOS, PLATFORMS,
  inr, inrK, num, pct, fmtDate,
  totals, buildRows, collectLeaves, nodeMetrics, groupOrganic,
} from './data';

/**
 * Channel Attribution — "members by channel" view.
 *
 * Rendered natively (no iframe) so it inherits the admin layout and blends with
 * the surrounding pages. The report's own CSS is scoped under `.favor-ca` (see
 * styles.js) to stay isolated from the global stylesheet. Typography uses the
 * admin app's own fonts (ivypresto-headline serif + indivisible sans) so the
 * tab is consistent with the surrounding pages.
 *
 * Live data: GET /admin/attribution/members-by-channel returns the same nested
 * tree as the placeholder `RAW`, plus `tiers`/`ratios`. An EMPTY tree is a real
 * answer (no sign ups matched the period/platform filter) and renders as zeros;
 * only a failed or malformed request falls back to the placeholder data, flagged
 * with the "sample data" chip.
 */
const RANGE_LABEL = { yesterday: 'Yesterday', '7d': 'Last 7 days', '30d': 'Last 30 days', campaign: 'Since launch' };
const GENDER_LABEL = { all: 'all genders', f: 'women only', m: 'men only' };
const PLATFORM_LABEL = { all: 'all platforms', app: 'Android app', ios: 'iOS app', web: 'Web' };

const iso = (d) => d.toISOString().slice(0, 10);
const TODAY = iso(new Date());
const shiftDays = (isoStr, n) => { const d = new Date(isoStr); d.setDate(d.getDate() + n); return iso(d); };

// Map the selected period to an inclusive {from,to} window the backend filters
// on. `campaign` ("since launch") leaves `from` open so it returns all history.
function rangeWindow(range, from, to) {
  switch (range) {
    case 'yesterday': { const y = shiftDays(TODAY, -1); return { from: y, to: y }; }
    case '30d': return { from: shiftDays(TODAY, -29), to: TODAY };
    case 'campaign': return { from: undefined, to: TODAY };
    case 'custom': return { from, to };
    case '7d':
    default: return { from: shiftDays(TODAY, -6), to: TODAY };
  }
}

const Seg = ({ items, value, onChange, dark }) => (
  <div className={`seg${dark ? ' dark' : ''}`}>
    {items.map(([key, label]) => (
      <button key={key} type="button" className={value === key ? 'on' : ''} onClick={() => onChange(key)}>
        {label}
      </button>
    ))}
  </div>
);

const ChannelAttribution = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState('7d');
  const [gender, setGender] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [from, setFrom] = useState(() => shiftDays(TODAY, -6));
  const [to, setTo] = useState(TODAY);
  const [expanded, setExpanded] = useState(() => new Set());

  // Ghost Users card → the full-page onboarding drop-off funnel. The current
  // period/gender/platform ride along as query params so the funnel opens on the
  // same view the user was looking at.
  const openFunnel = useCallback(() => {
    const q = new URLSearchParams();
    q.set('range', range);
    q.set('gender', gender === 'f' ? 'women' : gender === 'm' ? 'men' : 'all');
    q.set('platform', platform);
    if (range === 'custom') { q.set('from', from); q.set('to', to); }
    window.open(`/onboarding-dropoff?${q.toString()}`, '_blank', 'noopener,noreferrer');
  }, [range, gender, platform, from, to]);

  const [status, setStatus] = useState('loading'); // loading | live | placeholder
  const [fetching, setFetching] = useState(true);
  const [dataset, setDataset] = useState({ tree: RAW, prevTree: [], tiers: TIERS, ratios: RATIOS });

  // Pull the GROUP-BY from the backend; fall back to placeholder only on failure.
  // Period (from/to) and platform are filtered server-side; gender stays a
  // client-side slice since each leaf already carries both gender blocks.
  useEffect(() => {
    let active = true;
    setFetching(true);
    (async () => {
      try {
        const win = rangeWindow(range, from, to);
        const params = {};
        if (win.from) params.from = win.from;
        if (win.to) params.to = win.to;
        if (platform !== 'all') params.platform = platform;
        const res = await getRequest({ url: '/admin/attribution/members-by-channel', params });
        // Accept either a bare payload or the common { data: {...} } envelope.
        const body = res?.data;
        const payload = Array.isArray(body?.tree) ? body : body?.data;
        if (!active) return;
        if (payload && Array.isArray(payload.tree)) {
          // An empty tree / tiers / ratios means nobody matched the filter (e.g. "Web"
          // on a day with no web-acquired sign ups). Show that as zeros — never swap
          // the sample dataset in for it, which read as 254 fake sign ups.
          setDataset({
            tree: payload.tree,
            prevTree: Array.isArray(payload.prevTree) ? payload.prevTree : [],
            tiers: Array.isArray(payload.tiers) ? payload.tiers : [],
            ratios: Array.isArray(payload.ratios) ? payload.ratios : [],
          });
          setStatus('live');
        } else {
          setStatus('placeholder');
        }
      } catch (err) {
        if (active) setStatus('placeholder');
      } finally {
        if (active) setFetching(false);
      }
    })();
    return () => { active = false; };
  }, [range, from, to, platform]);

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        // Collapse this node and every descendant.
        next.delete(id);
        [...next].forEach((k) => { if (k.startsWith(id + '>')) next.delete(k); });
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Collapse the many non-paid channels the backend now emits back under a single
  // "Organic / Unknown" parent (each source kept as an expandable sub-row); Meta /
  // Google / Reddit stay top-level. Totals/leaves are unaffected (grouping only
  // inserts an intermediate node).
  const tree = useMemo(() => groupOrganic(dataset.tree), [dataset.tree]);
  // Period + platform are already applied server-side, so the client aggregation
  // runs with factor 1 and platform 'all' (no fake scaling/splitting); gender is
  // the only remaining client-side slice.
  const T = useMemo(() => totals(tree, gender, 1, 'all'), [tree, gender]);
  const P = useMemo(() => totals(dataset.prevTree || [], gender, 1, 'all'), [dataset.prevTree, gender]);
  // Pass the selected platform so the inline app/web breakdown rows (built from
  // the backend's real `_plat` split) only show in the "All platforms" view; when
  // a platform is already filtered, the tree is backend-filtered to it already.
  const allRows = useMemo(() => buildRows(tree, gender, 1, platform), [tree, gender, platform]);
  const leaves = useMemo(() => collectLeaves(tree, gender, 1, 'all'), [tree, gender]);

  // Only show rows whose every ancestor is expanded (rows are in DFS order).
  const visibleRows = useMemo(() => {
    const shown = new Set();
    const out = [];
    allRows.forEach((r) => {
      if (r.level === 0) { out.push(r); shown.add(r.id); return; }
      if (expanded.has(r.parent) && shown.has(r.parent)) { out.push(r); shown.add(r.id); }
    });
    return out;
  }, [allRows, expanded]);

  // CAC denominator excludes channels with NO ad spend (organic / influencer /
  // email / custom links): those active users weren't acquired by paid spend, so
  // counting them would dilute the "real" cost per active. Only spend-bearing
  // channels (Meta / Google / Reddit) count toward the denominator; the numerator
  // (T.spend) is already paid-only since non-paid channels carry zero spend.
  const paidActive = useMemo(
    () => (tree || []).reduce((s, ch) => {
      const m = nodeMetrics(ch, gender, 1, 'all');
      return s + (m.spend > 0 ? m.active : 0);
    }, 0),
    [tree, gender],
  );
  const prevPaidActive = useMemo(
    () => (dataset.prevTree || []).reduce((s, ch) => {
      const m = nodeMetrics(ch, gender, 1, 'all');
      return s + (m.spend > 0 ? m.active : 0);
    }, 0),
    [dataset.prevTree, gender],
  );
  const costActive = paidActive ? T.spend / paidActive : 0;
  const prevCostActive = prevPaidActive ? P.spend / prevPaidActive : 0;
  const ghostRate = pct(T.ghost, T.signups);
  // Of SIGN UPS, not of Active. Subscribed counts the cohort's paying members in whatever
  // status they hold today — lapsed, hidden, deactivated and deleted included — so most of
  // them are not in Active at all and the old `% of active` could read above 100%.
  const subRate = pct(T.subscribed, T.signups);

  // City-tier card follows the gender toggle: pick the selected gender's raw tier
  // counts (all = women + men) and compute the %/read client-side.
  const genderWord = gender === 'f' ? 'women' : gender === 'm' ? 'men' : 'members';
  const tierRows = useMemo(() => (dataset.tiers || []).map((t) => {
    const f = t.f || { t1: 0, t2: 0, t3: 0 };
    const m = t.m || { t1: 0, t2: 0, t3: 0 };
    const c = gender === 'f' ? f : gender === 'm' ? m : { t1: f.t1 + m.t1, t2: f.t2 + m.t2, t3: f.t3 + m.t3 };
    const total = c.t1 + c.t2 + c.t3;
    const p = (n) => (total ? Math.round((n / total) * 100) : 0);
    const t1 = p(c.t1), t2 = p(c.t2), t3 = p(c.t3);
    const read = t1 >= 55 ? ['Strong Tier 1', 'good'] : t1 >= 35 ? ['Mixed tiers', 'warn'] : ['Skews Tier 2/3', 'risk'];
    return { ...t, t1, t2, t3, total, read };
  }).filter((t) => t.total > 0).sort((a, b) => b.t1 - a.t1), [dataset.tiers, gender]);

  // Period-over-period delta vs the previous equal-length window (P). `goodWhenUp`
  // flips the colour for metrics where an increase is bad (ghost/hidden/cost).
  const hasPrev = (P.signups || 0) > 0;
  const deltaOf = (cur, prev, goodWhenUp = true) => {
    if (!hasPrev || !prev) return { delta: '', dir: 'flat' };
    const change = Math.round(((cur - prev) / prev) * 100);
    const dir = change === 0 ? 'flat' : (change > 0) === goodWhenUp ? 'up' : 'down';
    return { delta: `${change > 0 ? '+' : ''}${change}%`, dir };
  };

  const ctxLabel =
    (range === 'custom'
      ? `Custom · ${fmtDate(from)} – ${fmtDate(to)}`
      : RANGE_LABEL[range]) +
    ` · ${GENDER_LABEL[gender]} · ${PLATFORM_LABEL[platform]}`;

  const kpis = [
    { lab: 'Sign Ups', val: num(T.signups), note: 'top of funnel', cls: '', ...deltaOf(T.signups, P.signups) },
    // Active is not a status: it is Approved + Hidden, so its two parts sit right after it.
    { lab: 'Active Users', val: num(T.active), note: `approved + hidden · ${pct(T.active, T.signups)}% of sign ups`, cls: '', ...deltaOf(T.active, P.active) },
    { lab: 'Approved', val: num(T.approved), note: `visible on search · ${pct(T.approved, T.signups)}% of sign ups`, cls: 'good', ...deltaOf(T.approved, P.approved) },
    { lab: 'Hidden Users', val: num(T.hidden), note: `${pct(T.hidden, T.signups)}% of sign ups`, cls: '', ...deltaOf(T.hidden, P.hidden, false) },
    { lab: 'Inactive', val: num(T.inactive), note: `${pct(T.inactive, T.signups)}% of sign ups`, cls: 'inactk', ...deltaOf(T.inactive, P.inactive, false) },
    { lab: 'Subscribed', val: num(T.subscribed), note: `${subRate}% of sign ups`, cls: 'subk', ...deltaOf(T.subscribed, P.subscribed) },
    { lab: 'Ghost Users', val: num(T.ghost), note: `${ghostRate}% of sign ups · view drop-off →`, cls: 'warnk', click: openFunnel, ...deltaOf(T.ghost, P.ghost, false) },
    { lab: 'Deactivated', val: num(T.deactivated), note: `${pct(T.deactivated, T.signups)}% of sign ups`, cls: 'deactk', ...deltaOf(T.deactivated, P.deactivated, false) },
    { lab: 'Suspended', val: num(T.suspended), note: `${pct(T.suspended, T.signups)}% of sign ups`, cls: 'suspk', ...deltaOf(T.suspended, P.suspended, false) },
    { lab: 'Deleted', val: num(T.deleted), note: `${pct(T.deleted, T.signups)}% of sign ups`, cls: 'delk', ...deltaOf(T.deleted, P.deleted, false) },
    { lab: 'Ad Spend', val: inrK(T.spend), note: 'paid channels', cls: '', ...deltaOf(T.spend, P.spend) },
    { lab: 'Cost / Active User', val: inr(costActive), note: 'the real CAC', cls: 'alertk', ...deltaOf(costActive, prevCostActive, false) },
  ];

  const stages = [
    { name: 'Sign Ups', note: 'account created', val: T.signups, color: 'var(--slate)' },
    { name: 'Active Users', note: 'approved + hidden', val: T.active, color: 'var(--good)' },
    // Approved is the half of Active that is actually on member search, so it IS nested
    // inside the stage above it — unlike Subscribed. Shown next to Active everywhere else
    // on this screen (KPI strip, source table); listing it here keeps the funnel in sync.
    { name: 'Approved', note: 'visible on search right now', val: T.approved, color: 'var(--accent)' },
    { name: 'Subscribed', note: 'members who ever paid', val: T.subscribed, color: 'var(--plum)' },
  ];
  const maxV = stages[0].val || 1;

  const life = [
    { n: 'APPROVED', v: T.approved, c: 'var(--good)' },
    { n: 'HIDDEN', v: T.hidden, c: 'var(--slate)' },
    { n: 'INACTIVE', v: T.inactive, c: 'var(--rose)' },
    { n: 'GHOST', v: T.ghost, c: 'var(--warn)' },
    { n: 'DEACTIVATED', v: T.deactivated, c: 'var(--blue)' },
    { n: 'SUSPENDED', v: T.suspended, c: 'var(--violet)' },
    { n: 'DELETED', v: T.deleted, c: 'var(--accent)' },
  ];
  // The seven canonical statuses partition Sign Ups, so the status bar sums to Sign Ups =
  // Approved + Hidden + Inactive + Ghost + Deactivated + Suspended + Deleted (deleted folds
  // back into its signup cohort by signup date). Active (= Approved + Hidden) is NOT a
  // segment — it would double-count both. Subscribed cuts ACROSS all seven (an overlay:
  // a member who paid and then lapsed, hid or left is still counted), so it stays
  // legend-only — adding it to the bar would double-count every segment it touches.
  const legendItems = life.concat([
    { n: 'SUBSCRIBED', v: T.subscribed, c: 'var(--plum)' },
  ]);

  const heat = (v) => (v >= 55 ? 'var(--good)' : v >= 30 ? 'var(--warn)' : 'var(--accent)');
  const gtxt = gender === 'all' ? 'M+F' : gender === 'f' ? 'Women' : 'Men';
  // Level tag by attribution ROLE (not raw depth) — sources nested under the
  // "Organic / Unknown" group are still labelled "Channel".
  // The placeholder tree (RAW) embeds each node's level in its own name
  // ("Campaign · Sign ups – Women") because the badge beside it already says the level, so
  // the level word is stripped before display. It used to strip everything before ANY "· ",
  // which also ate real server-sent names containing that separator — the synthetic
  // "Channel spend · campaign unattributed" node rendered as just "campaign unattributed",
  // losing the half that says what it is. Anchored to the level words only.
  const stripLvlPrefix = (name) => String(name).replace(/^(Campaign|Ad group|Creative) · /, '');
  // What a row is CALLED on screen. `label` is the ad platform's own name for a campaign the
  // server merged with differently-named spend — e.g. sign ups arrive under utm_campaign
  // "iOS" while Reddit calls it "iOS Web Sign Ups | Favor | July 2026", and the second is
  // what someone reconciling against Reddit Ads is looking for. Display only: `name` remains
  // the value every filter and drill-through link uses.
  const rowLabel = (r) => stripLvlPrefix(r.label || r.name);
  const LVL_LABEL = { channel: 'Channel', campaign: 'Campaign', adset: 'Ad group', creative: 'Creative' };
  // The ad-set level holds a REAL ad-set / ad-group name on Meta, but on every other
  // channel it is the raw utm_medium ("ppc" / "cpc"). Badging that "Ad group" reads as
  // granularity we do not have — Google Performance Max in particular has NO ad groups at
  // all (it uses asset groups), so that row can never become a genuine ad-group split.
  // A row with spend but NO sign ups is the synthetic node carrying money we could not
  // join to any user — real campaign name, zero attributed people. Badging it "Campaign"
  // put it beside genuine campaigns as a peer, so Reddit read as two campaigns ("iOS" with
  // 13 sign ups and no spend, "iOS Web Sign Ups | Favor | July 2026" with ₹10,411 and no
  // sign ups) when it is one campaign whose user rows and spend rows carry different names.
  // They are NOT merged: there is no join key — no click id, nothing — so pairing them
  // would be a guess that reads as data. The channel row above already shows the correct
  // combined figures; this only stops the split halves impersonating separate campaigns.
  const isSpendOnly = (r) => r.m.signups < 1 && r.m.spend > 0;
  // buildTree parks a channel's own leaf under a synthetic "(direct)" child when the channel
  // ALSO has real campaigns, so those users are not silently dropped. It is not a campaign —
  // it is the people we know came from this channel but could not pin to any campaign — so
  // badging it "Campaign" put a non-campaign in the campaign list. Under Google that read as
  // a third campaign sitting beside PMax.
  const isDirect = (r) => r.role === 'campaign' && r.name === '(direct)';
  const lvlLabel = (r) => (r.kind === 'platform' ? 'Platform'
    : isDirect(r) ? 'No campaign'
    : isSpendOnly(r) ? 'Unattributed'
    : r.role === 'adset' && r.chKey !== 'meta' ? 'Medium'
    : LVL_LABEL[r.role]);

  // Walk a row up to its channel root to assemble the attribution node
  // (channel/campaign/adset/creative + platform) the members page filters on.
  const rowById = useMemo(() => new Map(allRows.map((r) => [r.id, r])), [allRows]);
  const buildViewHref = (row, status) => {
    const q = { gender };
    // Account-status bucket the clicked number represents. 'signups' is the
    // all-members default (same as the "View →" link), so it's left off the URL.
    if (status && status !== 'signups') q.status = status;
    // Carry the SAME period window the tree was computed with, so the drilled-in
    // members list matches the row's counts. `campaign` ("since launch") leaves
    // `from` open, so only `to` is sent — exactly like the tree query above.
    const win = rangeWindow(range, from, to);
    if (win.from) q.from = win.from;
    if (win.to) q.to = win.to;
    let cur = row;
    while (cur) {
      // Walk by ROLE, not raw depth: a source under the "Organic / Unknown" group is
      // one level deeper but still the CHANNEL. The group row itself (role 'orggroup')
      // carries no single channel key, so it contributes nothing to the filter.
      // A platform row's id ends in its PLATFORMS index (data.js walk).
      if (cur.kind === 'platform') q.platform = (PLATFORMS[Number(cur.id.slice(-1))] || PLATFORMS[0])[0];
      // Prefer the backend's real channel key; `cls` collapses custom channels to
      // 'd-organic', so deriving from it would send e.g. pinterest as 'organic'.
      // Fall back to the cls/name only for placeholder data (no `key`).
      else if (cur.role === 'channel') q.channel = cur.key || (cur.cls || '').replace('d-', '') || cur.name;
      else if (cur.role === 'campaign') q.campaign = cur.name;
      else if (cur.role === 'adset') q.adset = cur.name;
      else if (cur.role === 'creative') q.creative = cur.name;
      cur = rowById.get(cur.parent);
    }
    if (!q.platform && platform !== 'all') q.platform = platform;
    const qs = Object.entries(q)
      .filter(([, v]) => v != null && v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    return `/attribution-members?${qs}`;
  };

  // Export the full drill-down (all rows, not just the expanded ones) as CSV.
  const exportCsv = () => {
    // Active = Approved + Hidden (a total); the seven statuses after it sum to Sign Ups.
    // Ghost sits SECOND, straight after Sign Ups: it is the biggest single outcome of a
    // sign up and the first thing anyone judges a source on, so it reads next to the number
    // it is a share of. The remaining statuses keep canonical order after it.
    const head = ['Source', 'Sign Ups', 'Ghost', /* 'Completed (Ads Event)', */ /* 'Profile 100%', */ 'Active', 'Approved', 'Hidden', 'Inactive', 'Deactivated', 'Suspended', 'Deleted', 'Subscribed', 'Spend', 'Cost / active'];
    const lines = allRows.map((r) => {
      const lvlName = lvlLabel(r);
      const isTop = r.role === 'channel' || r.role === 'orggroup';
      const label = isTop ? `${'  '.repeat(r.level)}${r.name}` : `${'  '.repeat(r.level)}${lvlName}: ${rowLabel(r)}`;
      const costAct = r.m.active ? Math.round(r.m.spend / r.m.active) : '';
      return [label, r.m.signups, r.m.ghost, /* r.m.completed, */ /* r.m.filled, */ r.m.active, r.m.approved, r.m.hidden, r.m.inactive, r.m.deactivated, r.m.suspended, r.m.deleted, r.m.subscribed,
        Math.round(r.m.spend), r.m.spend > 0 ? costAct : ''];
    });
    const csv = [head, ...lines]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `channel-attribution-${range}-${TODAY}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ pb: 4 }}>
      <style>{styles}</style>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <HeadTitle
          imageSrc="/assets/images/admin-panel/analytics.png"
          text="Channel Attribution"
          description="Members by acquisition channel, campaign & creative — across the funnel"
        />
        {status === 'placeholder' && (
          <Chip size="small" color="warning" variant="outlined" label="Showing sample data — live source unavailable" />
        )}
      </Stack>

      <div className="favor-ca">
        <div className="controls">
          <div className="ctl-group">
            <span className="lbl">Period</span>
            <Seg
              items={[['yesterday', 'Yesterday'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['campaign', 'Since launch'], ['custom', 'Custom']]}
              value={range}
              onChange={setRange}
            />
            {range === 'custom' && (
              <span className="daterange">
                <input type="date" max={TODAY} value={from} onChange={(e) => { const v = e.target.value; setFrom(v); if (v > to) setTo(v); }} />
                <span className="dsep">to</span>
                <input type="date" max={TODAY} value={to} onChange={(e) => { const v = e.target.value; setTo(v); if (v < from) setFrom(v); }} />
              </span>
            )}
          </div>
          <div className="ctl-group">
            <span className="lbl">Gender</span>
            <Seg items={[['all', 'All'], ['f', 'Women'], ['m', 'Men']]} value={gender} onChange={setGender} dark />
          </div>
          <div className="ctl-group">
            <span className="lbl">Platform</span>
            <Seg items={[['all', 'All'], ['app', 'Android app'], ['ios', 'iOS app'], ['web', 'Web']]} value={platform} onChange={setPlatform} dark />
          </div>
          <button type="button" className="btn-export" onClick={exportCsv}>Export</button>
        </div>
        <div className="ctxline">
          Showing: <b>{ctxLabel}</b> &nbsp;·&nbsp; status &amp; cost-per-active are backend-fed; app attributed via
          AppsFlyer, web via UTM capture; spend is allocated to a platform by its sign-up share (ads aren’t natively
          platform-tagged).
        </div>

        {/* Only the content (not the filter bar) shows the loader on a refetch, so
            the date/gender/platform controls stay visible and interactive. The area
            fills the rest of the viewport so the spinner centres over the whole
            content region (no short box leaving the bottom blank). */}
        {fetching ? (
          <div style={{ minHeight: 'calc(100vh - 240px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader />
          </div>
        ) : (
          <>
        {/* KPIs */}
        <section className="kpis">
          {kpis.map((k) => (
            <div
              key={k.lab}
              className={`kpi ${k.cls}${k.click ? ' clickable' : ''}`}
              onClick={k.click}
              role={k.click ? 'button' : undefined}
              tabIndex={k.click ? 0 : undefined}
              onKeyDown={k.click ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); k.click(); } } : undefined}
            >
              <div className="lab">{k.lab}</div>
              <div className="val serif">{k.val}<span className={`delta ${k.dir}`}>{k.delta}</span></div>
              <div className="note">{k.note}</div>
            </div>
          ))}
        </section>

        {/* Acquisition & member status */}
        <div className="sec"><h2>Acquisition &amp; member status</h2><span className="hint">sign up → active → approved → subscribed</span></div>
        <div className="grid-2">
          <div className="card">
            <h3>From sign up to a paying member</h3>
            <div className="desc">The drops between stages are the leaks — sign ups who never go active, actives who are not visible on search, and sign ups who never pay. Approved IS a subset of Active (its other half is Hidden). Subscribed is counted against the whole cohort, not against Approved, so it is not nested inside the stage above it: some of these members paid and have since lapsed, hidden or left.</div>
            <div>
              {stages.map((s, i) => {
                const w = Math.max((s.val / maxV) * 100, 3);
                const prev = i > 0 ? stages[i - 1].val : null;
                const ch = prev ? Math.round(((s.val - prev) / prev) * 100) : 0;
                return (
                  <div className="frow" key={s.name}>
                    <div className="ft">
                      <div className="fn">{s.name}<span>{s.note}</span></div>
                      <div className="fv serif">{num(s.val)} · {pct(s.val, maxV)}%</div>
                    </div>
                    <div className="bar"><div className="fill" style={{ width: `${w}%`, background: s.color }} /></div>
                    {i > 0 && <div className="drop">{ch}% vs {stages[i - 1].name.toLowerCase()}</div>}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="card">
            <h3>Account status breakdown</h3>
            <div className="desc">Where every sign up sits today. Active (Approved + Hidden) is not a slice of this bar, so the bar adds up to sign ups. Subscribed cuts across all seven statuses — a member who paid counts here whether they are still approved, hidden, inactive, deactivated or gone.</div>
            <div className="statusbar">
              {life.map((s) => {
                const p = pct(s.v, T.signups);
                if (p < 1) return null;
                return (
                  <div
                    className="seg-block"
                    key={s.n}
                    style={{ width: `${p}%`, background: s.c }}
                    title={`${s.n.charAt(0) + s.n.slice(1).toLowerCase()}: ${num(s.v)} (${p}%)`}
                  >
                    {/* Show count + % on every segment. Both are short so they fit (and may
                        overflow narrow segments via overflow:visible) without cropping. The
                        bucket NAME lives in the legend below (matched by color dot). */}
                    <span className="sb-pct">{num(s.v)}</span>
                    <span className="sb-name">{p}%</span>
                  </div>
                );
              })}
            </div>
            <div className="legend">
              {legendItems.map((s) => {
                const nm = s.n.charAt(0) + s.n.slice(1).toLowerCase();
                return (
                  <span key={s.n}>
                    <i style={{ background: s.c }} />{nm} <b>{num(s.v)}</b>
                    {s.n === 'SUBSCRIBED' && <span style={{ color: 'var(--faint)' }}> (across all states)</span>}
                  </span>
                );
              })}
            </div>
            <div className="statusbar-label" style={{ marginTop: 18 }}>Marketplace ratio (active members)</div>
            <div className="ratios">
              {dataset.ratios.length === 0 && (
                <div className="mkt-ratio"><div className="rv">—</div><div className="rw">no members in this view</div></div>
              )}
              {dataset.ratios.map((r) => (
                <div className="mkt-ratio" key={r.when}>
                  <div className="rv" style={{ color: `var(--${r.cls})` }}>{r.val}</div>
                  <div className="rw">{r.when}</div>
                </div>
              ))}
            </div>
            <div className="callout amber">
              <b>{ghostRate}% of sign ups are ghosts</b> and {pct(T.hidden + T.inactive, T.signups)}% are hidden or
              inactive — finished signup but not on search. They cost full acquisition price and return nothing; only the {num(T.subscribed)}{' '}
              <b>subscribed</b> members ever earned revenue.
            </div>
          </div>
        </div>

        {/* Performance by source */}
        <div className="sec"><h2>Performance by source</h2><span className="hint">click a row to drill: channel → campaign → ad group (Meta) / medium → creative → platform</span></div>
        <div className="card">
          <h3>Where members come from — and what status they reach</h3>
          <div className="desc">First-touch attribution. Expand any row to the level below. Status counts are backend-fed; spend from the ad platforms. “View” opens the matching profiles to monitor them.</div>
          <div className="desc" style={{ marginTop: -4 }}>
            <b>Sign Ups = Approved + Hidden + Inactive + Ghost + Deactivated + Suspended + Deleted</b> — every user
            their campaign acquired, counted on their signup date whatever their status is today (same canonical
            statuses as the Signup Report, Dashboard and User Management). <b>Active</b> is not a status: it is
            Approved (visible on search) + Hidden (complete, not on search), shown as a total and not added again.{' '}
            {/* The Completed (Ads Event) and Profile 100% columns are commented out below, so their
                explainers are dropped from this blurb too. */}
            <i>Subscribed</i> is an overlay, not a funnel bucket, so it doesn’t add into
            Sign Ups.{' '}
            Deleted counts the separate deleted-profiles log on each user’s signup date (their original cohort);{' '}
            <b>Subscribed</b> is every member of the cohort who ever paid — the same ledger rule as the Subscription
            Report (a plan that reached Active/Cancelled/Expired and cleared a real charge), counted whatever their
            status is today. So it spans every column to its right, deleted included: a member a campaign bought and
            converted keeps earning that campaign credit after they lapse or leave. It is deliberately NOT the
            <i> isSubscribed</i> flag, which is only who is paying right now.
          </div>
          <div className="tablewrap stickyhead">
            <table>
              <thead>
                <tr>
                  <th className="l">Source</th><th>Sign Ups</th><th title="never finished signup — the biggest single outcome, so it reads next to Sign Ups">Ghost</th>{/* <th>Completed (Ads Event)</th> */}{/* <th>Profile 100%</th> */}<th title="Approved + Hidden">Active</th><th>Approved</th><th>Hidden</th><th>Inactive</th>
                  <th>Deactiv.</th><th>Suspend.</th><th>Deleted</th><th>Subscribed</th><th>Spend</th><th>Cost / active</th><th className="l">Profiles</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 && (
                  <tr><td className="l" colSpan={14}>No sign ups match this filter for the selected period.</td></tr>
                )}
                {visibleRows.map((r) => {
                  const indent = 8 + r.level * 20;
                  const open = expanded.has(r.id);
                  const isTop = r.role === 'channel' || r.role === 'orggroup';
                  const lvlName = lvlLabel(r);
                  const costAct = r.m.active ? r.m.spend / r.m.active : 0;
                  // The "Organic / Unknown" group spans several channels, which the
                  // members endpoint can't match in one query, so its counts aren't
                  // links — drill into a source sub-row to view its members.
                  // A spend-only row (the synthetic "campaign unattributed" node) has no
                  // members BY DEFINITION, so it gets no "View" either — the whole point of
                  // the row is that this money bought nobody we can name.
                  const linkable = r.role !== 'orggroup' && r.m.signups >= 1;
                  const viewHref = linkable ? buildViewHref(r) : null;
                  // Each status count opens the SAME members list as "View →" but
                  // scoped to that one account-status bucket (backend filters on the
                  // tree's own STATUS_BUCKET_EXPR, so the list count == this cell).
                  // Zero counts render as a plain number (nothing to open).
                  const statCell = (val, status, cls) => (
                    <td className={cls}>
                      {val > 0 && linkable ? (
                        <a
                          className="numlink"
                          href={buildViewHref(r, status)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {num(val)}
                        </a>
                      ) : num(val)}
                    </td>
                  );
                  return (
                    <tr
                      key={r.id}
                      className={`treerow lvl${r.level}${r.hasKids ? ' haskids' : ''}`}
                      onClick={r.hasKids ? () => toggle(r.id) : undefined}
                    >
                      <td className="l">
                        <span className="nm" style={{ paddingLeft: indent }}>
                          <span className={`chev${r.hasKids ? '' : ' none'}${open ? ' open' : ''}`}>›</span>
                          {isTop ? (
                            <span className="chan"><span className={`cdot ${r.cls}`} />{r.name}</span>
                          ) : r.kind === 'platform' ? (
                            <span><span className="lvltag">{lvlName}</span><span className={`cdot ${r.cls}`} style={{ marginRight: 7 }} />{r.name}</span>
                          ) : (
                            <span><span className={`lvltag${isSpendOnly(r) || isDirect(r) ? ' unattr' : ''}`}>{lvlName}</span>{rowLabel(r)}</span>
                          )}
                        </span>
                      </td>
                      {statCell(r.m.signups, 'signups')}
                      {/* {statCell(r.m.completed, 'completed', 'pct-good')} */}
                      {/* Profile 100% = completion 100 AND a real profile picture
                          (CANON_PROFILE_COMPLETE server-side). An OVERLAY, not a status
                          bucket: these members also sit in exactly one of the seven status columns to
                          the right, so this one does not add into Sign Ups. It counts the
                          deleted cohort too (they count toward Sign Ups), which is why the
                          drill-through unions the delete-log for this status. The photo test
                          is what keeps this cell equal to the list it opens — UserList renders
                          a photo-less 100 as 95, so counting those here put rows reading 95
                          inside a list headed "Profile 100% complete".
                          Column commented out on request — the server still sends `filled`,
                          so restoring it is uncommenting these four spots (head, CSV row,
                          th, this cell) plus the tfoot total and the blurb above. */}
                      {/* {statCell(r.m.filled, 'filled', 'pct-fill')} */}
                      {/* Ghost second, right after the Sign Ups it is a share of. */}
                      {statCell(r.m.ghost, 'ghost', 'pct-mid')}
                      {/* Active = Approved + Hidden, a total (status=active drills the same union). */}
                      {statCell(r.m.active, 'active')}
                      {statCell(r.m.approved, 'approved', 'pct-good')}
                      {statCell(r.m.hidden, 'hidden', 'pct-slate')}
                      {statCell(r.m.inactive, 'inactive', 'pct-rose')}
                      {statCell(r.m.deactivated, 'deactivated')}
                      {statCell(r.m.suspended, 'suspended')}
                      {/* Deleted users live in a SEPARATE collection (the delete-log),
                          so this opens the DeletedAccountList (not UserList) — Members.js
                          switches on status==='deleted'. Backend scopes the delete-log to
                          the node via buildNodeMatch so the list count == this cell. */}
                      {statCell(r.m.deleted, 'deleted', 'pct-bad')}
                      {statCell(r.m.subscribed, 'subscribed', 'pct-sub')}
                      <td>{r.m.spend > 0 ? inr(r.m.spend) : '—'}</td>
                      <td>{r.m.active && r.m.spend > 0 ? inr(costAct) : '—'}</td>
                      <td className="l">
                        {viewHref ? (
                          <a className="viewlink" href={viewHref} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>View →</a>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="l">Total · all channels</td>
                  <td>{num(T.signups)}</td><td>{num(T.ghost)}</td>{/* <td>{num(T.completed)}</td> */}{/* <td>{num(T.filled)}</td> */}<td>{num(T.active)}</td><td>{num(T.approved)}</td><td>{num(T.hidden)}</td><td>{num(T.inactive)}</td>
                  <td>{num(T.deactivated)}</td><td>{num(T.suspended)}</td><td>{num(T.deleted)}</td><td>{num(T.subscribed)}</td>
                  <td>{inr(T.spend)}</td><td>{inr(costActive)}</td><td className="l" />
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="callout red">
            <span className="tag verify">VERIFY</span><b>Ghost</b>, <b>hidden</b> and <b>deleted</b> rates expose
            creatives that buy cheap sign ups who never become real members. A creative can have a low
            cost-per-sign-up yet a terrible cost-per-active — and <b>Subscribed</b> is the only column that earns
            revenue. Judge sources on the right-hand columns, not sign ups alone.
          </div>
        </div>

        {/* Channel × city-tier */}
        <div className="sec"><h2>Channel × city-tier — where members come from</h2><span className="hint">are we buying target metros, or cheap Tier 2/3 sign ups?</span></div>
        <div className="grid-2b">
          <div className="card">
            <h3>Where each channel's {genderWord} come from</h3>
            <div className="desc">Share of each channel's active <b>{genderWord}</b> by city tier. Tier&nbsp;1 = target metros.</div>
            <div className="tablewrap">
              <table style={{ minWidth: 'auto' }}>
                <thead><tr><th className="l">Channel</th><th>Tier 1</th><th>Tier 2</th><th>Tier 3</th><th className="l">Tier read</th></tr></thead>
                <tbody>
                  {tierRows.length === 0 && (
                    <tr><td className="l" colSpan={5}>No active paid-channel {genderWord} in this view.</td></tr>
                  )}
                  {tierRows.map((t) => {
                    const mini = (v) => (
                      <span className="meter">
                        <span className="mt">{v}%</span>
                        <span className="track"><span className="fill" style={{ width: `${v}%`, background: heat(v) }} /></span>
                      </span>
                    );
                    return (
                      <tr key={t.key}>
                        <td className="l"><span className="chan"><span className={`cdot ${t.cls}`} />{t.name}</span></td>
                        <td>{mini(t.t1)}</td><td>{mini(t.t2)}</td><td>{mini(t.t3)}</td>
                        <td className="l"><span className={`pill ${t.read[1]}`}>{t.read[0]}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card">
            <h3>Top creatives by paying members</h3>
            <div className="desc">Ranked by subscribers won (current gender view) — the creatives that bring members who actually pay, counted whether or not those members are still active.</div>
            <div className="tablewrap">
              <table style={{ minWidth: 'auto' }}>
                <thead><tr><th className="l">Creative</th><th className="l">Gender</th><th>Sign Ups</th><th title="Approved + Hidden">Active</th><th title="visible on member search right now">Approved</th><th>Subs</th><th title="paying members as a share of this creative's sign ups">Sub rate</th><th>Cost / active</th></tr></thead>
                <tbody>
                  {leaves.length === 0 && (
                    <tr><td className="l" colSpan={8}>No creatives with sign ups in this view.</td></tr>
                  )}
                  {leaves.slice(0, 7).map((l, i) => {
                    const ca = l.m.active ? l.m.spend / l.m.active : 0;
                    // Per sign up, matching the Subscribed KPI: this creative's paying members
                    // include ones no longer in its Active count, so dividing by Active can
                    // exceed 100% and would rank a creative higher for LOSING its actives.
                    const lSubRate = l.m.signups ? Math.round((l.m.subscribed / l.m.signups) * 100) : 0;
                    return (
                      <tr key={`${l.name}-${i}`}>
                        <td className="l">{l.name}</td>
                        <td className="l"><span className="pill neutral">{gtxt}</span></td>
                        <td>{num(l.m.signups)}</td>
                        <td>{num(l.m.active)}</td>
                        {/* The Approved half of Active — same column the source table and the
                            KPI strip show, so a creative reads the same on every panel. */}
                        <td className="pct-good">{num(l.m.approved)}</td>
                        <td className="pct-sub">{num(l.m.subscribed)}</td>
                        <td>{lSubRate}%</td>
                        <td>{l.m.spend > 0 ? inr(ca) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="foot">
          <span>Favor (Pinuxi Digital Pvt. Ltd.) · attribution = first-touch · status from backend, spend from AppsFlyer / ad platforms</span>
          <span>First-touch, write-once · app via AppsFlyer · web via UTM capture</span>
        </div>
          </>
        )}
      </div>
    </Box>
  );
};

export default ChannelAttribution;
